import { InputLabel, makeStyles, Tooltip } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import Portal from '../../../../portal/Portal';
import Image from '../../../Image';
import Table from '../../../Table';
import cn from 'classnames';
import { isEmpty } from 'lodash';
import fieldSelection from '..';
import update from 'immutability-helper';
import get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import AddCircleOutlineSharpIcon from '@material-ui/icons/AddCircleOutlineSharp';
import { FORM } from 'utils/constants/css';

const useStyles = makeStyles(() => ({
  formContainer: {
    marginBottom: '1rem',
  },
  labelContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    paddingBottom: '0.5rem',
  },
  img: {
    padding: FORM.PADDING_BUTTON,
    boxSizing: 'border-box',
  },
}));

const EmbeddedForm = ({
  item,
  formProps,
  className,
  componentProps,
  allowDrag,
  additionalFields,
  archiveDelete,
  deleteReferenceKey = 'id',
  onDeleteCallback,
}) => {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [editModeValues, setEditModeValues] = useState(null);
  const [subTasks, setSubTasks] = useState(formProps.values.subtasks);
  // ------------
  // This method of updating component state comes from the React docs.
  // They describe it as a potential future usePrevious Hook using useRefs.
  //
  const previousValueRef = useRef();
  const previousValue = previousValueRef.current;
  if (
    formProps.values.subtasks !== previousValue &&
    formProps.values.subtasks !== subTasks
  ) {
    setSubTasks(formProps.values.subtasks);
  }
  useEffect(() => {
    previousValueRef.current = formProps.values.subtasks;
  });

  useEffect(() => {
    // reorder the text and sequence number
    if (formProps.values?.subtasks && subTasks.length > 0) {
      // set the subtasks
      formProps.setFieldValue('subtasks', subTasks);

      // reorder the additional markers according to the order of the subtasks
      const reOrderedAdditionalMarkers =
        formProps.values?.additionalMarkers &&
        formProps.values.additionalMarkers
          .map(addMark => {
            // find the index of the the addMark's subtask in the subTasks array
            const indX = subTasks.findIndex(f => f.id === addMark.subtaskId);
            if (indX === -1) return addMark; // this means that the subtasks were copied from template and doesnot need reordering
            return {
              ...addMark,
              seqNumber: indX + 1,
              text: indX + 1,
            };
          })
          .sort((a, b) => a.seqNumber - b.seqNumber);

      formProps.setFieldValue('additionalMarkers', reOrderedAdditionalMarkers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTasks]);

  const classes = useStyles();

  const closeForm = () => {
    setShowForm(false);
    setEditModeValues(null);
  };

  const toggleForm = () => {
    setShowForm(!showForm);
  };

  const onDelete = val => {
    const values = formProps.values[item.field];

    let afterDelete = [];
    // This is for deleting tasks in event reports
    if (val.rootTaskId && item.field === 'followUptask') {
      afterDelete = values.filter(f => f.rootTaskId !== val.rootTaskId);
      if (afterDelete.length === 0) {
        afterDelete = undefined;
      }
    } else {
      afterDelete = values.filter(item => {
        const deleteItemId = get(item, deleteReferenceKey);
        const toBeDeletedId = get(val, deleteReferenceKey);
        return deleteItemId !== toBeDeletedId;
      });
    }

    formProps.setFieldValue(item.field, afterDelete);
    const deletedValues = formProps.values[`deleted${item.field}`] || [];
    formProps.setFieldValue(
      `deleted${item.field}`,
      deletedValues.concat({
        ...val,
        ...(archiveDelete && { archived: true }),
      }),
    );

    // siteSurfaceImages is present in sub-tasks but not in schedule,
    //so it is good check for making sure that it only deletes additional markers provided the location exists in parent

    if (
      val?.siteSurfaceImages &&
      val?.location &&
      val?.location.length > 0 &&
      formProps.values.additionalMarkers
    ) {
      formProps.setFieldValue(
        'additionalMarkers',
        formProps.values.additionalMarkers.filter(
          f => f.id !== val.location[0].id,
        ),
      );
    }

    if (typeof onDeleteCallback === 'function') {
      onDeleteCallback({
        formProps,
        formItem: item,
        value: val,
      });
    }

    if (val?.subtasks) {
      setSubTasks(afterDelete);
    }
  };

  const onEdit = data => {
    toggleForm();
    setEditModeValues(data);
  };

  const changeAdditionalMarkerText = (dragIndex, hoverIndex) => {
    const dragSubTaskRow = subTasks[dragIndex];
    // sub task reorder
    setSubTasks(
      update(subTasks, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragSubTaskRow],
        ],
      }),
    );
  };

  const { [item.field]: subFormValues } = formProps.values;

  const hasSubFormValues = subFormValues && subFormValues.length > 0;

  return (
    <div className={cn(classes.formContainer, className)}>
      <div className={classes.labelContainer}>
        <InputLabel shrink required={item.required}>
          {t(item.label)}
          {item.info && <Tooltip text={item.info} />}
        </InputLabel>
        <Image
          title={item.label}
          onClick={toggleForm}
          className={classes.img}
          text={`Add ${item.label}`}
          icon={<AddCircleOutlineSharpIcon />}
        />
      </div>
      {additionalFields &&
        additionalFields.map((field, key) => {
          if (field.condition && typeof field.condition === 'function') {
            return field.condition(formProps.values)
              ? fieldSelection({ item: field, key, props: formProps })
              : null;
          }
          return fieldSelection({ item: field, key, props: formProps });
        })}
      {hasSubFormValues && !showForm && (
        <Table
          allowEditRow
          allowDeleteRow
          columns={item.columns}
          data={
            item.dataTransform && subFormValues
              ? item.dataTransform(subFormValues)
              : subFormValues
          }
          onDelete={onDelete}
          onEdit={onEdit}
          changeAdditionalMarkerText={changeAdditionalMarkerText}
          allowDrag={allowDrag}
        />
      )}
      {showForm && (
        <Portal id="portal" type="modal">
          <item.component
            editMode={!isEmpty(editModeValues)}
            editModeValues={editModeValues}
            defaultValues={item.defaultValues}
            closeForm={closeForm}
            formProps={formProps}
            formItem={item}
            open={showForm}
            {...componentProps}
          />
        </Portal>
      )}
    </div>
  );
};

EmbeddedForm.propTypes = {
  item: PropTypes.object,
  formProps: PropTypes.object,
  className: PropTypes.string,
  component: PropTypes.object,
  componentProps: PropTypes.object,
  additionalFields: PropTypes.array,
  archiveDelete: PropTypes.bool,
  allowDrag: PropTypes.bool,
  deleteReferenceKey: PropTypes.string,
  onDeleteCallback: PropTypes.func,
};

export default EmbeddedForm;
